import { useState, useCallback } from "react";
import { authFetch } from "../utils/authFetch";

/** A single access rule: slug path + include_children flag. */
export interface AccessRule {
  id?: string;
  slug: string;
  includeChildren: boolean;
}

/** Format a rule for the API payload */
function ruleToApi(r: AccessRule) {
  return { slug: r.slug, include_children: r.includeChildren };
}

// ── Default doc access ──────────────────────────────────────────────────────

export function useDefaultDocAccess(projectId: string) {
  const [rules, setRules] = useState<AccessRule[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const res = await authFetch(`/api/admin/doc-access/default/${projectId}`);
    if (res.ok) setRules(await res.json());
    setLoading(false);
  }, [projectId]);

  const save = useCallback(async (items: AccessRule[]) => {
    const res = await authFetch(`/api/admin/doc-access/default/${projectId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rules: items.map(ruleToApi) }),
    });
    if (res.ok) await load();
    return res.ok;
  }, [projectId, load]);

  const clear = useCallback(async () => {
    const res = await authFetch(`/api/admin/doc-access/default/${projectId}`, { method: "DELETE" });
    if (res.ok) setRules([]);
    return res.ok;
  }, [projectId]);

  return { rules, loading, load, save, clear };
}

// ── Partner doc access ──────────────────────────────────────────────────────

export interface PartnerAccessConfig {
  /** null: the partner has not been given this project. */
  mode: "default" | "default_plus" | "override" | null;
  rules: AccessRule[];
  defaultRules: AccessRule[];
}

export function usePartnerDocAccess(partnerId: string, projectId: string) {
  const [config, setConfig] = useState<PartnerAccessConfig>({ mode: null, rules: [], defaultRules: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!partnerId || !projectId) return;
    setLoading(true);
    const res = await authFetch(`/api/admin/partners/${partnerId}/doc-access/${projectId}`);
    if (res.ok) setConfig(await res.json());
    setLoading(false);
  }, [partnerId, projectId]);

  const save = useCallback(async (mode: string, items: AccessRule[]) => {
    const res = await authFetch(`/api/admin/partners/${partnerId}/doc-access/${projectId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode, rules: items.map(ruleToApi) }),
    });
    if (res.ok) await load();
    return res.ok;
  }, [partnerId, projectId, load]);

  const clear = useCallback(async () => {
    const res = await authFetch(`/api/admin/partners/${partnerId}/doc-access/${projectId}`, { method: "DELETE" });
    if (res.ok) await load();
    return res.ok;
  }, [partnerId, projectId, load]);

  return { config, loading, load, save, clear };
}

// ── User doc access ─────────────────────────────────────────────────────────

export interface UserAccessConfig {
  mode: "default" | "default_plus" | "override";
  rules: AccessRule[];
  defaultRules: AccessRule[];
}

export function useUserDocAccess(userId: string, projectId: string) {
  const [config, setConfig] = useState<UserAccessConfig>({ mode: "default", rules: [], defaultRules: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!userId || !projectId) return;
    setLoading(true);
    const res = await authFetch(`/api/admin/users/${userId}/doc-access/${projectId}`);
    if (res.ok) setConfig(await res.json());
    setLoading(false);
  }, [userId, projectId]);

  const save = useCallback(async (mode: string, items: AccessRule[]) => {
    const res = await authFetch(`/api/admin/users/${userId}/doc-access/${projectId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode, rules: items.map(ruleToApi) }),
    });
    if (res.ok) await load();
    return res.ok;
  }, [userId, projectId, load]);

  const clear = useCallback(async () => {
    const res = await authFetch(`/api/admin/users/${userId}/doc-access/${projectId}`, { method: "DELETE" });
    if (res.ok) setConfig({ mode: "default", rules: [], defaultRules: [] });
    return res.ok;
  }, [userId, projectId]);

  return { config, loading, load, save, clear };
}
